class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');

    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.debounceTimer = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();

    clearTimeout(this.debounceTimer);

    // 🔒 mínimo 2 caracteres
    if (newSearchTerm.length < 2) {
      this.close(true);
      return;
    }

    // 🧠 debounce real
    this.debounceTimer = setTimeout(() => {
      super.onChange();

      if (
        !this.searchTerm ||
        !newSearchTerm.startsWith(this.searchTerm)
      ) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;

      this.getSearchResults(this.searchTerm);
    }, 300);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (currentSearchTerm.length < 2) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    if (!searchForTextElement) return;

    const currentText = searchForTextElement.innerText;
    if (!currentText) return;

    searchForTextElement.innerText = currentText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter(el => el.offsetParent !== null);

    let index = selectedElement
      ? allVisibleElements.indexOf(selectedElement)
      : -1;

    if (moveUp) {
      index = index <= 0 ? allVisibleElements.length - 1 : index - 1;
    } else {
      index = index === allVisibleElements.length - 1 ? 0 : index + 1;
    }

    allVisibleElements.forEach(el => el.setAttribute('aria-selected', false));
    const active = allVisibleElements[index];
    if (!active) return;

    active.setAttribute('aria-selected', true);
    this.input.setAttribute('aria-activedescendant', active.id);
  }

  selectOption() {
    const selected = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selected) selected.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,query&resources[limit]=6&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then(response => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(text => {
        const markup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search')?.innerHTML;

        if (!markup) return;

        this.allPredictiveSearchInstances.forEach(instance => {
          instance.cachedResults[queryKey] = markup;
        });

        this.renderSearchResults(markup);
      })
      .catch(error => {
        if (error?.code === 20) return;
        this.close();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    if (this.statusElement) {
      this.statusElement.textContent = this.loadingText;
      this.statusElement.setAttribute('aria-hidden', 'false');
    }

    this.setAttribute('loading', true);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    this.open();
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      `${window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom}px`;

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
  if (clearSearchTerm) {
    this.removeAttribute('results');
  }

    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);

(() => {
  // Si la clase no existe en este scope, no hacemos nada.
  if (typeof PredictiveSearch === 'undefined') return;

  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  function highlightTextInElement(el, term) {
    if (!el || !term || term.length < 2) return;

    // Si Shopify ya mandó <mark> en styled_text, solo lo estilizamos por CSS.
    if (el.querySelector && el.querySelector('mark')) return;

    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
        if (!node.nodeValue.toLowerCase().includes(term.toLowerCase())) return NodeFilter.FILTER_REJECT;
        // Evitar textos dentro de script/style
        const p = node.parentElement;
        if (p && (p.closest('script,style'))) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    const re = new RegExp(escapeRegExp(term), 'ig');

    nodes.forEach((textNode) => {
      const text = textNode.nodeValue;
      const matches = [...text.matchAll(re)];
      if (!matches.length) return;

      const frag = document.createDocumentFragment();
      let lastIdx = 0;

      matches.forEach((m) => {
        const idx = m.index;
        const partBefore = text.slice(lastIdx, idx);
        if (partBefore) frag.appendChild(document.createTextNode(partBefore));

        const mark = document.createElement('mark');
        mark.className = 'ps-hl';
        mark.textContent = text.slice(idx, idx + m[0].length);
        frag.appendChild(mark);

        lastIdx = idx + m[0].length;
      });

      const rest = text.slice(lastIdx);
      if (rest) frag.appendChild(document.createTextNode(rest));

      textNode.parentNode.replaceChild(frag, textNode);
    });
  }

  function addLazyAttrs(root) {
    root?.querySelectorAll?.('img.predictive-search__image').forEach((img) => {
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
    });
  }

  function ensureSkeleton(root) {
    if (!root) return;
    const results = root.querySelector('#predictive-search-results');
    if (!results) return;

    if (results.querySelector('.ps-skel')) return;

    results.insertAdjacentHTML(
      'afterbegin',
      `
      <div class="ps-skel" aria-hidden="true">
        <div class="ps-skel__grid">
          <div class="ps-skel__col">
            <div class="ps-skel__h"></div>
            <div class="ps-skel__line"></div>
            <div class="ps-skel__line"></div>
            <div class="ps-skel__line"></div>
          </div>
          <div class="ps-skel__col">
            <div class="ps-skel__h"></div>
            <div class="ps-skel__row"></div>
            <div class="ps-skel__row"></div>
            <div class="ps-skel__row"></div>
          </div>
        </div>
      </div>
      `
    );
  }

  // 1) Evitar “flash” y resultados viejos: abortar fetch anterior SIEMPRE.
  const _getSearchResults = PredictiveSearch.prototype.getSearchResults;
  PredictiveSearch.prototype.getSearchResults = function (searchTerm) {
    try { this.abortController.abort(); } catch (e) {}
    this.abortController = new AbortController();
    return _getSearchResults.call(this, searchTerm);
  };

  // 2) Quitar el “remove()” que te deja el dropdown vacío milisegundos.
  //    (lo reemplazamos por skeleton overlay)
  const _onChange = PredictiveSearch.prototype.onChange;
  PredictiveSearch.prototype.onChange = function () {
    const newSearchTerm = this.getQuery();

    // Si va a cambiar fuerte, NO borres el wrapper; pon skeleton y espera el fetch.
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.setLiveRegionLoadingState?.();
      ensureSkeleton(this);
    }

    return _onChange.call(this);
  };

  // 3) Post-procesado: highlight + lazy imgs cada vez que se renderiza
  const _renderSearchResults = PredictiveSearch.prototype.renderSearchResults;
  PredictiveSearch.prototype.renderSearchResults = function (resultsMarkup) {
    _renderSearchResults.call(this, resultsMarkup);

    // after DOM updated
    const root = this.predictiveSearchResults || this.querySelector('[data-predictive-search]');
    addLazyAttrs(root);

    const term = this.getQuery?.() || '';
    root?.querySelectorAll?.('.predictive-search__item-heading, .predictive-search__item-query-result').forEach((el) => {
      highlightTextInElement(el, term);
    });
  };

  // 4) Skeleton: garantizar que exista cuando loading
  const _setLiveRegionLoadingState = PredictiveSearch.prototype.setLiveRegionLoadingState;
  PredictiveSearch.prototype.setLiveRegionLoadingState = function () {
    _setLiveRegionLoadingState.call(this);
    ensureSkeleton(this);
  };
})();

